/* Holsper cart drawer: "Complete your bed" suggestions (snippets/holsper-cart-drawer.liquid).
   Asks Shopify's recommendations for up to 3 products in the cart (most expensive first) and renders
   sections/holsper-cart-recs.liquid. For each product, until the panel is full:
   1. its complementary products (set in Search & Discovery), then
   2. its related products, only of the product types listed in the theme editor
      (so a bedding set suggests pillowcases and sheets, not another set or a duvet cover), same colour first.
   Results are kept per cart, so the panel does not flash when the theme redraws the cart drawer. A size window opened from the panel closes itself once the product is added. */
(() => {
  if (window.customElements.get('hx-cart-recs')) return;

  const CACHE = new Map();
  let lastHtml = '';
  const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';

  // "Hotel Black - Bedding Set (Easy Change)" -> "hotel black"
  const colourOf = (title) => String(title || '').replace(/\s[\u2013\u2014]\s/g, ' - ').split(' - ')[0].trim().toLowerCase();

  const fetchCards = (productId, intent, limit) =>
    fetch(`${root}recommendations/products?product_id=${productId}&limit=${limit}&intent=${intent}&section_id=holsper-cart-recs`, { priority: 'low' })
      .then((r) => (r.ok ? r.text() : ''))
      .then((t) => Array.from(new DOMParser().parseFromString(t, 'text/html').querySelectorAll('[data-hx-crec]')))
      .catch(() => []);

  class HxCartRecs extends HTMLElement {
    connectedCallback() {
      this.list = this.querySelector('[data-hx-crecs-list]');
      if (!this.list) return;
      const key = this.getAttribute('key') || '';
      if (CACHE.has(key)) {
        this.show(CACHE.get(key));
        return;
      }
      // While new suggestions load, keep the previous ones (minus anything now in the cart) so the panel doesn't jump.
      if (lastHtml) this.show(this.withoutInCart(lastHtml));
      this.load(key);
    }

    get inCart() {
      return new Set((this.getAttribute('products') || '').split(',').filter(Boolean));
    }

    withoutInCart(html) {
      const tpl = document.createElement('template');
      tpl.innerHTML = html;
      const inCart = this.inCart;
      tpl.content.querySelectorAll('[data-hx-crec]').forEach((card) => {
        if (inCart.has(card.dataset.productId)) card.remove();
      });
      return tpl.innerHTML.trim();
    }

    async load(key) {
      const ids = (this.getAttribute('products') || '').split(',').filter(Boolean).slice(0, 3);
      const limit = parseInt(this.getAttribute('limit'), 10) || 3;
      if (!ids.length) return this.show('');

      const allowTypes = new Set((this.getAttribute('allow-types') || '').split(',').map((t) => t.trim().toLowerCase()).filter(Boolean));
      const inCart = this.inCart;
      const colour = colourOf(this.getAttribute('colour'));
      const seen = new Set();
      const picks = [];
      const consider = (card, related) => {
        const id = card.dataset.productId;
        if (picks.length >= limit || seen.has(id) || inCart.has(id)) return;
        if (related && allowTypes.size && !allowTypes.has((card.dataset.type || '').trim().toLowerCase())) return;
        seen.add(id);
        picks.push(card.outerHTML);
      };

      // Product by product (main product first): its complementary products, then its related ones.
      for (const id of ids) {
        if (picks.length >= limit) break;
        const [complementary, related] = await Promise.all([fetchCards(id, 'complementary', 6), fetchCards(id, 'related', 10)]);
        complementary.forEach((c) => consider(c, false));
        // Same colour first: "Hotel Black - Flat Sheet" before "Dusty Mauve - Flat Sheet" for a Hotel Black set
        const sameColour = (c) => colour && colourOf(c.dataset.title) === colour;
        [...related.filter(sameColour), ...related.filter((c) => !sameColour(c))].forEach((c) => consider(c, true));
      }

      const html = picks.join('');
      CACHE.set(key, html);
      if (this.isConnected) this.show(html);
    }

    show(html) {
      if (html) lastHtml = html;
      this.list.innerHTML = html;
      this.hidden = !html;
    }
  }

  window.customElements.define('hx-cart-recs', HxCartRecs);

  // Added from a size window opened in the panel: close it, so the customer sees the updated cart.
  document.addEventListener('variant:add', (event) => {
    // composedPath: the theme swaps the window's content during this event, so look at the path the event took
    const drawer = event.composedPath().find((el) => el.matches && el.matches('quick-buy-drawer[id^="hx-crec-"]'));
    if (drawer && typeof drawer.hide === 'function') setTimeout(() => drawer.hide(), 60);
  });
})();
